import { parseNumber } from '@progress/kendo-intl';
import { PDFViewerHandle, scrollToPage } from '@progress/kendo-react-pdf-viewer';

import axios from 'axios';
import { round } from 'lodash';
import { PDFDocument, PDFPage, rgb } from 'pdf-lib';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ImagePreview from 'src/components/Image';
import PDFViewer from 'src/components/PDFViewer';
import useWindowSize from 'src/hooks/useWindowSize';
import { OcrInvoiceResponse } from 'src/services/useGetOcrResponseApi';
import { uint8ArrayToBase64 } from 'src/utils/fileTransformation';
import { formatOcrData } from 'src/utils/transformation';

export interface IOcrData {
  field: string;
  value: string | null;
  pageNumber: string | null;
  boundingBox: number[] | null;
  confidence: string | null;
  isHighlight?: boolean;
}
interface OcrInvoiceProps {
  src: string;
  data: OcrInvoiceResponse;
  isPDF: boolean;
  fileName: string;
  invoiceOcrConfidence: string | null;
}

const pdfWidth = 595; // this width OCR service using for convert pdf
const pdfHeight = 841; // this height OCR service using for convert pdf

// Bounding box colors
const COLORS = {
  highlight: { hex: '#FF00E6', rgb: rgb(1, 0, 0.9) },
  matched: { hex: '#94C947', rgb: rgb(0.58, 0.78, 0.27) },
  unmatched: { hex: '#D9AA4F', rgb: rgb(0.85, 0.66, 0.3) },
} as const;

/** Calculate bounding rectangle from 8-point coordinates */
const calculateBoundingRect = (coords: number[]) => {
  const [x1, y1, x2, y2, x3, y3, x4, y4] = coords;
  const minX = Math.min(x1, x2, x3, x4);
  const minY = Math.min(y1, y2, y3, y4);
  const maxX = Math.max(x1, x2, x3, x4);
  const maxY = Math.max(y1, y2, y3, y4);
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

/** Expand rectangle by 1.5x for highlight effect */
const applyHighlightExpansion = (rect: { x: number; y: number; width: number; height: number }) => {
  const expandW = (rect.width * 1.5 - rect.width) / 2;
  const expandH = (rect.height * 1.5 - rect.height) / 2;
  return {
    x: rect.x - expandW,
    y: rect.y - expandH,
    width: rect.width + expandW * 2,
    height: rect.height + expandH * 2,
  };
};

const getPdfWidthAndHeightBasedOnRotation = (page: PDFPage) => {
  const { width, height } = page.getSize();
  const rotation = page.getRotation().angle;
  if ((rotation / 90) % 2 === 1) {
    return {
      width: height,
      height: width,
    };
  }
  return {
    width,
    height,
  };
};

export const ocrInvoiceFields = [
  'invoiceNumber',
  'purchaseOrder',
  'invoiceDate',
  'amountDue',
  'totalTax',
  'totalDiscount',
  'totalShipping',
];

const OcrInvoice: FC<OcrInvoiceProps> = ({ src, data, isPDF, fileName, invoiceOcrConfidence }) => {
  const { height, width } = useWindowSize();
  const [pdfData, setPdfData] = useState(''); // base64 bbox data
  const [pdfUrl, setPdfUrl] = useState(''); // original pdf url
  const [imageUrl, setImageUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [highlightPage, setHighlightPage] = useState(1);
  const pdfRef = useRef<PDFViewerHandle | null>(null);

  // Cache downloaded file to avoid re-fetching when only data changes
  const cachedFileRef = useRef<{
    src: string;
    arrayBuffer: ArrayBuffer;
    objectUrl: string;
  } | null>(null);

  // Pre-compute confidence threshold once (not per-item in loop)
  const confidenceThreshold = useMemo(
    () => parseNumber(invoiceOcrConfidence || '0%'),
    [invoiceOcrConfidence]
  );

  const generateImageWithBoundingBox = useCallback(
    async (imageSrc: string, ocrData: OcrInvoiceResponse) => {
      try {
        // Use cached file if src hasn't changed
        let objectUrl: string;
        if (cachedFileRef.current?.src === imageSrc) {
          ({ objectUrl } = cachedFileRef.current);
        } else {
          // Fetch and cache the new file
          const { data: arrayBuffer } = await axios.get<ArrayBuffer>(imageSrc, {
            responseType: 'arraybuffer',
          });

          // Revoke old cached URL if exists
          if (cachedFileRef.current?.objectUrl) {
            URL.revokeObjectURL(cachedFileRef.current.objectUrl);
          }

          objectUrl = URL.createObjectURL(new Blob([arrayBuffer]));
          cachedFileRef.current = { src: imageSrc, arrayBuffer, objectUrl };
        }

        const canvas = document.createElement('canvas');
        canvas.setAttribute('class', 'canv');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }

        const image = new Image();
        image.src = objectUrl;

        image.onload = () => {
          try {
            // Set canvas size to the image size
            canvas.width = image.width;
            canvas.height = image.height;

            // Draw the image on the canvas
            ctx.drawImage(image, 0, 0);

            // Draw the bounding boxes
            const formattedOcrData = formatOcrData(ocrData);
            formattedOcrData.forEach((item) => {
              if (item.boundingBox && item.boundingBox.length === 8) {
                const isMatchConfidence =
                  parseNumber(item.confidence || '0%') >= confidenceThreshold;

                // converting the coordinate into px base on the OCR service pdf Width and Height
                const mappedBox = item.boundingBox.map((value, index) => {
                  if (index % 2 === 0) {
                    return ((value * 72) / pdfWidth) * canvas.width;
                  } else {
                    return ((value * 72) / pdfHeight) * canvas.height;
                  }
                });

                let rect = calculateBoundingRect(mappedBox);
                if (item.isHighlight) {
                  rect = applyHighlightExpansion(rect);
                }

                ctx.strokeStyle = item.isHighlight
                  ? COLORS.highlight.hex
                  : isMatchConfidence
                  ? COLORS.matched.hex
                  : COLORS.unmatched.hex;
                ctx.lineWidth = item.isHighlight ? 2 : 1;
                ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
              }
            });

            const img = canvas.toDataURL('image/png', 1);
            setImageUrl(img);
            setIsLoading(false);
          } catch (error) {
            toast.error('Failed to process OCR data. Showing original image.');
            setImageUrl(objectUrl);
            setIsLoading(false);
          }
        };

        image.onerror = () => {
          toast.error('Failed to load image.');
          setIsLoading(false);
        };
      } catch (error) {
        toast.error('Failed to process image. Please try again.');
        setIsLoading(false);
      }
    },
    [confidenceThreshold]
  );

  const generatePDFWithBoundingBox = useCallback(
    async (pdfSrc: string, ocrData: OcrInvoiceResponse) => {
      try {
        // Use cached file if src hasn't changed
        let arrayBuffer: ArrayBuffer;
        let objectUrl: string;

        if (cachedFileRef.current?.src === pdfSrc) {
          // Use cached data - create a copy of ArrayBuffer since PDFDocument.load consumes it
          const { arrayBuffer: cachedBuffer, objectUrl: cachedUrl } = cachedFileRef.current;
          arrayBuffer = cachedBuffer.slice(0);
          objectUrl = cachedUrl;
        } else {
          // Fetch and cache the new file
          const response = await fetch(pdfSrc);
          if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.status}`);
          arrayBuffer = await response.arrayBuffer();

          // Revoke old cached URL if exists
          if (cachedFileRef.current?.objectUrl) {
            URL.revokeObjectURL(cachedFileRef.current.objectUrl);
          }

          const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
          objectUrl = URL.createObjectURL(blob);

          // Cache the original ArrayBuffer (make a copy since we'll modify it)
          cachedFileRef.current = { src: pdfSrc, arrayBuffer: arrayBuffer.slice(0), objectUrl };
        }

        setPdfUrl(objectUrl);

        const pdf = await PDFDocument.load(arrayBuffer);
        const pages = pdf.getPages();
        const formattedOcrData = formatOcrData(ocrData);

        for (let i = 1; i <= pages.length; i++) {
          const page = pages[i - 1];
          const { width: pageWidth, height: pageHeight } =
            getPdfWidthAndHeightBasedOnRotation(page);
          const rotation = page.getRotation().angle;

          formattedOcrData.forEach((item) => {
            if (
              Number(item.pageNumber) === i &&
              item.boundingBox &&
              item.boundingBox.length === 8
            ) {
              const mappedBox = item.boundingBox.map((value) => value * 72); // convert inch to pt
              const isMatchConfidence = parseNumber(item.confidence || '0%') >= confidenceThreshold;

              const baseRect = calculateBoundingRect(mappedBox);

              // Convert coordinates based on pdf page rotation
              let rect: typeof baseRect;
              switch (rotation) {
                case 90:
                  rect = {
                    x: baseRect.y,
                    y: baseRect.x,
                    width: baseRect.height,
                    height: baseRect.width,
                  };
                  break;
                case 180:
                  rect = {
                    x: pageWidth - baseRect.x - baseRect.width,
                    y: baseRect.y,
                    width: baseRect.width,
                    height: baseRect.height,
                  };
                  break;
                case 270:
                  rect = {
                    x: pageHeight - baseRect.y - baseRect.height,
                    y: pageWidth - baseRect.x - baseRect.width,
                    width: baseRect.height,
                    height: baseRect.width,
                  };
                  break;
                default: // 0 or other
                  rect = {
                    x: baseRect.x,
                    y: pageHeight - baseRect.y - baseRect.height,
                    width: baseRect.width,
                    height: baseRect.height,
                  };
              }

              if (item.isHighlight) {
                rect = applyHighlightExpansion(rect);
                setHighlightPage(Number(item.pageNumber));
              }

              page.drawRectangle({
                x: round(rect.x, 2),
                y: round(rect.y, 2),
                width: round(rect.width, 2),
                height: round(rect.height, 2),
                borderColor: item.isHighlight
                  ? COLORS.highlight.rgb
                  : isMatchConfidence
                  ? COLORS.matched.rgb
                  : COLORS.unmatched.rgb,
                borderWidth: item.isHighlight ? 2 : 1,
              });
            }
          });
        }

        const pdfBytesWithBoundingBoxes = await pdf.save();
        const dataUrl = uint8ArrayToBase64(pdfBytesWithBoundingBoxes);
        setPdfData(dataUrl);
        setIsLoading(false);
      } catch (error) {
        toast.error('Failed to process OCR data. Showing original PDF.');

        // Fallback: show original PDF without bounding boxes
        try {
          let fallbackBuffer: ArrayBuffer;
          let fallbackUrl: string;

          // Try to use cached data first
          if (cachedFileRef.current?.src === pdfSrc) {
            fallbackBuffer = cachedFileRef.current.arrayBuffer.slice(0);
            fallbackUrl = cachedFileRef.current.objectUrl;
          } else {
            // Fetch fresh if not cached
            const response = await fetch(pdfSrc);
            if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
            fallbackBuffer = await response.arrayBuffer();
            fallbackUrl = URL.createObjectURL(
              new Blob([fallbackBuffer], { type: 'application/pdf' })
            );
          }

          // Set pdfData (base64) for PDFViewer rendering
          setPdfData(uint8ArrayToBase64(new Uint8Array(fallbackBuffer)));
          setPdfUrl(fallbackUrl);
        } catch {
          console.error('Failed to load original PDF');
          toast.error('Failed to load PDF.');
        }

        setIsLoading(false);
      }
    },
    [confidenceThreshold]
  );

  // Cleanup cached file URL on unmount
  useEffect(
    () => () => {
      if (cachedFileRef.current?.objectUrl) {
        URL.revokeObjectURL(cachedFileRef.current.objectUrl);
        cachedFileRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (src && data && Object.keys(data).length > 0) {
      if (isPDF) {
        generatePDFWithBoundingBox(src, data);
      } else {
        generateImageWithBoundingBox(src, data);
      }
    }
  }, [isPDF, generateImageWithBoundingBox, generatePDFWithBoundingBox, src, data]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!isLoading) {
      timer = setTimeout(() => {
        if (pdfRef.current) {
          scrollToPage(pdfRef?.current?.element as HTMLElement, highlightPage - 1);
        }
      }, 300);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [highlightPage, isLoading, pdfRef, data]);

  return (
    <>
      {isLoading ? (
        <div className="w-full h-24 flex items-center justify-center">
          <span className="k-icon k-i-loading text-xl" />
        </div>
      ) : isPDF ? (
        <PDFViewer
          data={pdfData}
          url={pdfUrl}
          height={`${height - 125}px`}
          fileName={fileName}
          ref={pdfRef}
        />
      ) : (
        <ImagePreview
          imageUrl={imageUrl}
          controls
          filename={fileName}
          styles={{
            width: `${width}px`,
            maxHeight: `${height - 210}px`,
          }}
        />
      )}
    </>
  );
};

export default OcrInvoice;
